import {
  type EtatNoyauTexte,
  NOYAU_CODES,
  type NoyauCode,
  type NoyauxTexte,
  noyauxTexteSchema,
} from '@rosetta/shared';
import { z } from 'zod';
import type { ConfigTahiti } from '../config';
import { ServiceIndisponibleError } from '../erreurs';
import { analyser } from './analyse';
import { type CorrespondanceTypes, TYPE_TRADUCTION, lireSousTaches, noyauDuTitre } from './etapes';
import { CODE_LANGUE, idSchema, lireLignes } from './h2data';
import type { AppelHttp } from './http';
import type { ResultatLu } from './resultats';
import {
  type CorrespondanceStatuts,
  type StatutTache,
  TABLE_TACHE,
  champNoyau,
  classerStatut,
} from './taches';

/**
 * Where each noyau stands for the text of a translation (#11): what the choice of the noyaux to
 * add shows ("déjà traduit", "en cours", "interrompue", "n'a pas abouti", or free), and what
 * keeps a noyau already translated from being paid for twice.
 *
 * A text is a `rose_document`; translating it towards another noyau is a new run
 * (`rose_task_v5_multi_translation`) on the same document. So the state of a noyau is read over
 * every run of the document, never on one run alone: on the translation sub-task of that noyau
 * (`rose_task_v5_traduction`, under the run by `h2_task_main_id`) and on the
 * `rose_document_translation` rows of the document, the most recent row per noyau.
 *
 * TO VALIDATE(#11): what follows was read on devdb on 2026-10-08 (document 897172, taken up by
 * seventeen runs; runs 908461 and 910633), not played with the account of an end user: that the
 * runs of a document can be read by `rose_document_id`; that a row of `rose_document_translation`
 * is written only once the translation of its noyau has ended well and names its sub-task in
 * `h2_task_id`; that the title of a translation sub-task names its noyau (otherwise only a run
 * towards one noyau tells which one it is); and that the creator of a run is in
 * `h2_user_creator_id`.
 */

const booleen = z.boolean().nullish();

export const ligneLancementSchema = z.object({
  id: idSchema,
  status: z.string().min(1),
  h2_user_creator_id: idSchema.nullish(),
  target_core_sgp: booleen,
  target_core_spg: booleen,
  target_core_psg: booleen,
  target_core_pgs: booleen,
  target_core_gps: booleen,
  target_core_gsp: booleen,
});
type LigneLancement = z.infer<typeof ligneLancementSchema>;

/** One run of a document: its status, ranked like any translation, and the noyaux it targets. */
export interface LancementDocument {
  id: string;
  statut: StatutTache;
  noyaux: NoyauCode[];
}

/** More runs than one text will ever get: one, then one per noyau added. */
const LIMITE_LANCEMENTS = 200;

/**
 * Reads the runs of a document that belong to the user: the history only shows the user's runs,
 * and the choice of the noyaux says the same. A run whose creator Tahiti does not give is kept.
 */
export async function lireLancementsDocument(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  documentId: string,
  utilisateurId: string,
  statuts: CorrespondanceStatuts | undefined,
): Promise<LancementDocument[]> {
  const lignes = await lireLignes(
    appel,
    config,
    jeton,
    {
      table: TABLE_TACHE,
      fields: ['id', 'status', 'h2_user_creator_id', ...NOYAU_CODES.map(champNoyau)],
      where: ['rose_document_id', Number(documentId), '='],
      limit: LIMITE_LANCEMENTS,
      lang: CODE_LANGUE,
    },
    ligneLancementSchema,
  );
  return lignes
    .filter(
      (ligne) => ligne.h2_user_creator_id == null || ligne.h2_user_creator_id === utilisateurId,
    )
    .map((ligne) => {
      const choisis = NOYAU_CODES.filter(
        (code) => ligne[champNoyau(code) as keyof LigneLancement] === true,
      );
      return {
        id: ligne.id,
        statut: classerStatut(ligne.status, statuts),
        // Tahiti's own rule, as in `lireTache`: no noyau selected means the six of them.
        noyaux: choisis.length > 0 ? choisis : [...NOYAU_CODES],
      };
    });
}

/** The translation sub-task of one noyau in a run, ranked like a task. */
export interface TraductionNoyau {
  id: string;
  statut: StatutTache;
}

/**
 * The translation sub-tasks of a run, by noyau (the most recent one when a noyau has several):
 * the noyau is the one its title names, or the only one the run targets. `undefined` when Tahiti
 * does not let the sub-tasks be read (the state of the run then stands for its noyaux).
 */
export async function lireTraductionsLancement(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  lancement: LancementDocument,
  types: CorrespondanceTypes,
  statuts: CorrespondanceStatuts | undefined,
): Promise<Map<NoyauCode, TraductionNoyau> | undefined> {
  let lignes: Awaited<ReturnType<typeof lireSousTaches>>;
  try {
    lignes = await lireSousTaches(appel, config, jeton, lancement.id);
  } catch (erreur) {
    if (erreur instanceof ServiceIndisponibleError) return undefined;
    throw erreur;
  }

  const [seul] = lancement.noyaux.length === 1 ? lancement.noyaux : [];
  const parNoyau = new Map<NoyauCode, TraductionNoyau>();
  for (const ligne of lignes) {
    if (ligne.h2_tasktype_id == null || types.get(ligne.h2_tasktype_id) !== TYPE_TRADUCTION) {
      continue;
    }
    const noyau = noyauDuTitre(ligne.title) ?? seul;
    if (noyau === undefined) continue;
    const connue = parNoyau.get(noyau);
    if (connue === undefined || Number(ligne.id) > Number(connue.id)) {
      parNoyau.set(noyau, { id: ligne.id, statut: classerStatut(ligne.status, statuts) });
    }
  }
  return parNoyau;
}

/** What `etatsNoyaux` needs to know about the runs of a document. */
export interface ElementsNoyaux {
  lancements: readonly LancementDocument[];
  /** The translation sub-tasks of each run, by its identifier; absent when they could not be read. */
  traductions: ReadonlyMap<string, ReadonlyMap<NoyauCode, TraductionNoyau> | undefined>;
  /** The most recent row with a text of each noyau of the document (`lireResultats`). */
  resultats: readonly ResultatLu[];
}

/** An outcome of a run (or of one of its sub-tasks) that is over, as a state of the noyau. */
function issue(statut: StatutTache): EtatNoyauTexte {
  return statut === 'interrompue' ? 'interrompu' : 'echoue';
}

/**
 * The state of each noyau, from the runs of the document (a pure function, tested):
 *  1. `en_cours` when a run that targets it is still running and its sub-task for that noyau is
 *     not over (or not created yet);
 *  2. `traduit` when the most recent row of the noyau has a text and its sub-task, when it is
 *     known, has ended well;
 *  3. otherwise what the most recent run that targets it says for it: its sub-task (cancelled:
 *     `interrompu`, failed or ended without a row: `echoue`), or the run itself when there is no
 *     sub-task to read;
 *  4. `libre` when no run targets it.
 */
export function etatsNoyaux({ lancements, traductions, resultats }: ElementsNoyaux): NoyauxTexte {
  const sousTaches = new Map<string, TraductionNoyau>();
  for (const parNoyau of traductions.values()) {
    for (const traduction of parNoyau?.values() ?? []) sousTaches.set(traduction.id, traduction);
  }
  const recents = [...lancements].sort((a, b) => Number(b.id) - Number(a.id));

  const etat = (noyau: NoyauCode): EtatNoyauTexte => {
    const visant = recents.filter((lancement) => lancement.noyaux.includes(noyau));

    const enCours = visant.some((lancement) => {
      if (lancement.statut !== 'en_cours') return false;
      const sousTache = traductions.get(lancement.id)?.get(noyau);
      return sousTache === undefined || sousTache.statut === 'en_cours';
    });
    if (enCours) return 'en_cours';

    const resultat = resultats.find((lu) => lu.noyau === noyau);
    if (resultat !== undefined) {
      const sousTache =
        resultat.sousTacheId === undefined ? undefined : sousTaches.get(resultat.sousTacheId);
      if (sousTache === undefined || sousTache.statut === 'terminee') return 'traduit';
    }

    const [dernier] = visant;
    if (dernier === undefined) return 'libre';
    const sousTache = traductions.get(dernier.id)?.get(noyau);
    if (sousTache !== undefined && sousTache.statut !== 'en_cours') return issue(sousTache.statut);
    return issue(dernier.statut);
  };

  return analyser(
    noyauxTexteSchema,
    NOYAU_CODES.map((noyau) => ({ noyau, etat: etat(noyau) })),
    'noyaux of a text',
  );
}
